// Section 8 — "Verification" (explains-page-contract.json
// s7_verification): passed/max-deviation data line, signed center-zero
// deviation bars by asset class, and the deviation-reading table built
// from the contract's conditional templates.
import { Card, Table } from "react-bootstrap";
import { formatCurrency, formatPercent } from "../../../../lib/numbers";
import type { SoftSectionContext } from "./SoftCompletionContext";
import { CHART_COLORS, SignedDeviationChart } from "./charts";
import {
  UNKNOWN_AC,
  formatSignedPp,
  num,
  ownModelAccountIds,
  unreachableAcsForAccount,
} from "./softCompletionLib";

// Below this absolute deviation (pp) a class reads as on target.
const ON_TARGET_PP = 0.5;

interface DeviationRow {
  ac: string;
  label: string;
  pp: number;
  reading: string;
}

// Conditional reading per signed deviation (contract deviation_reading):
// over + named in a residual warning -> gains-locked block; under +
// pool ran dry -> capital limit; under + unreachable (E1 or client-side
// fallback) -> only household-following accounts can hold it.
function buildRows(ctx: SoftSectionContext): DeviationRow[] {
  const { completion } = ctx;
  const verification = completion.verification;
  const warningsByAc = new Map(
    (completion.overshoot_redistribution?.warnings ?? []).map((w) => [w.ac, w]),
  );
  const progression = completion.constrained_pool_progression ?? [];
  const lastResidual =
    progression[progression.length - 1]?.residual_by_asset_class ?? {};
  const anyScaled = Object.values(completion.residual_models ?? {}).some(
    (m) => m.scaled,
  );
  const unreachable = new Set(
    ownModelAccountIds(completion).flatMap((accountId) =>
      unreachableAcsForAccount(
        accountId,
        completion,
        ctx.accountsById,
        ctx.symbolToAc,
      ),
    ),
  );

  return Object.entries(verification?.deviations_by_asset_class ?? {})
    .map(([ac, v]) => {
      const pp = num(v) * 100;
      const warning = warningsByAc.get(ac);
      const unfilled = Math.max(num(lastResidual[ac]), 0);
      let reading = "—";
      if (ac === UNKNOWN_AC) {
        reading = "cash targets";
      } else if (Math.abs(pp) < ON_TARGET_PP) {
        reading = "on target";
      } else if (pp > 0 && warning != null) {
        reading = `gains-locked block — the ${formatCurrency(num(warning.residual_dollars))} residual warning`;
      } else if (pp < 0 && anyScaled && unfilled > 0) {
        reading = `completion pool exhausted — ${formatCurrency(unfilled)} unfilled`;
      } else if (pp < 0 && unreachable.has(ac)) {
        reading = "only household-following accounts can hold it";
      }
      return {
        ac,
        label: ac === UNKNOWN_AC ? "unknown (cash)" : ac,
        pp,
        reading,
      };
    })
    .sort((a, b) => Math.abs(b.pp) - Math.abs(a.pp));
}

export default function VerificationSection({
  ctx,
}: {
  ctx: SoftSectionContext;
}) {
  const verification = ctx.completion.verification;
  if (verification == null) return null;
  const rows = buildRows(ctx);

  return (
    <Card className="mb-3">
      <Card.Header>Verification — household vs target</Card.Header>
      <Card.Body>
        <p className="small mb-2">
          <code>passed = {String(verification.passed)}</code> ·{" "}
          <code>
            max_deviation = {formatPercent(num(verification.max_deviation), 2)}
          </code>
        </p>

        <SignedDeviationChart
          title="Final deviation from household target by asset class"
          rows={rows.map((r) => ({
            label: r.label,
            value: r.pp,
            color: r.ac === UNKNOWN_AC ? CHART_COLORS.cash : ctx.acColor(r.ac),
          }))}
        />

        {rows.length > 0 && (
          <Table size="sm" responsive className="mt-2 mb-0">
            <caption className="text-muted small">
              Deviation reading per asset class
            </caption>
            <thead>
              <tr>
                <th>Asset class</th>
                <th className="text-end">Deviation</th>
                <th>Reading</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ac}>
                  <td>{r.label}</td>
                  <td
                    className={`text-end ${
                      Math.abs(r.pp) >= ON_TARGET_PP ? "text-danger" : ""
                    }`}
                  >
                    {formatSignedPp(r.pp)}
                  </td>
                  <td>{r.reading}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}
